import { useState, useEffect, useCallback } from 'react';
import {
  listVectorExports,
  getVectorExport,
  createVectorExport,
  syncVectorExport,
  deleteVectorExport,
  getVectorExportUsageExample,
  getDatasetVectorExports
} from '../services/vectorExportsApi';

interface VectorExportsParams {
  dataset_id?: string;
  status?: string;
  page?: number;
  limit?: number;
  [key: string]: string | number | undefined;
}

/**
 * Custom hook for managing vector exports list
 * @param params - Filter parameters
 * @param params.dataset_id - Filter by dataset ID
 * @param params.status - Filter by status
 * @returns Vector exports state and operations
 */
export const useVectorExports = (params: VectorExportsParams = {}) => {
  const [exports, setExports] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);

  const fetchExports = useCallback(async (overrideParams: VectorExportsParams = {}): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await listVectorExports({
        page,
        ...params,
        ...overrideParams
      });
      setExports(response.data || response);
      if (response.meta) {
        setTotalPages(response.meta.totalPages || 1);
        setTotal(response.meta.total || response.data?.length || 0);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching vector exports:', err);
    } finally {
      setLoading(false);
    }
  }, [page, params]);

  useEffect(() => {
    fetchExports();
  }, [fetchExports]);

  const create = useCallback(async (exportData: Record<string, any>): Promise<any> => {
    setError(null);
    try {
      const result = await createVectorExport(exportData as any);
      // Refresh list after creating
      await fetchExports();
      return result;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  }, [fetchExports]);

  const remove = useCallback(async (exportId: string, deleteAwsResources: boolean = false): Promise<void> => {
    setError(null);
    try {
      await deleteVectorExport(exportId, deleteAwsResources);
      // Refresh list after deleting
      await fetchExports();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  }, [fetchExports]);

  return {
    exports,
    loading,
    error,
    page,
    setPage,
    totalPages,
    total,
    refresh: fetchExports,
    create,
    remove
  };
};

/**
 * Custom hook for managing a single vector export
 * @param exportId - Vector export ID
 * @returns Vector export state and operations
 */
export const useVectorExport = (exportId: string | null | undefined) => {
  const [vectorExport, setVectorExport] = useState<Record<string, any> | null>(null);
  const [usageExample, setUsageExample] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExport = useCallback(async (): Promise<void> => {
    if (!exportId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getVectorExport(exportId);
      setVectorExport(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching vector export:', err);
    } finally {
      setLoading(false);
    }
  }, [exportId]);

  const fetchUsageExample = useCallback(async (): Promise<void> => {
    if (!exportId) return;

    try {
      const data = await getVectorExportUsageExample(exportId);
      setUsageExample(data);
    } catch (err: unknown) {
      console.error('Error fetching usage example:', err);
    }
  }, [exportId]);

  useEffect(() => {
    fetchExport();
  }, [fetchExport]);

  useEffect(() => {
    if (vectorExport?.status === 'completed') {
      fetchUsageExample();
    }
  }, [vectorExport?.status, fetchUsageExample]);

  const sync = useCallback(async (): Promise<any> => {
    if (!exportId) return;

    setSyncing(true);
    setError(null);

    try {
      const result = await syncVectorExport(exportId);
      setVectorExport(result);
      return result;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    } finally {
      setSyncing(false);
    }
  }, [exportId]);

  const remove = useCallback(async (deleteAwsResources: boolean = false): Promise<void> => {
    if (!exportId) return;

    try {
      await deleteVectorExport(exportId, deleteAwsResources);
      setVectorExport(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      throw err;
    }
  }, [exportId]);

  return {
    vectorExport,
    usageExample,
    loading,
    syncing,
    error,
    sync,
    remove,
    refresh: fetchExport,
    refreshUsageExample: fetchUsageExample
  };
};

/**
 * Custom hook for vector exports of a specific dataset
 * @param datasetId - Dataset ID
 * @returns Dataset vector exports state
 */
export const useDatasetVectorExports = (datasetId: string | null | undefined) => {
  const [exports, setExports] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExports = useCallback(async (): Promise<void> => {
    if (!datasetId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await getDatasetVectorExports(datasetId);
      setExports(data.data || data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error fetching dataset vector exports:', err);
    } finally {
      setLoading(false);
    }
  }, [datasetId]);

  useEffect(() => {
    fetchExports();
  }, [fetchExports]);

  return {
    exports,
    loading,
    error,
    refresh: fetchExports
  };
};

export default useVectorExports;
